import type { TasteVerdict, TtqPipelineStatus, TtqSessionParticleEntry } from '../services/ttqService';
import { getParticleTitle } from './particleDisplay';
import { formatParticlePreviewPlainText } from './particlePreviewText';

const IMPLICIT_ABILITY_LABEL = '隐式能力包';

/** 把 title/questions 等未知值收成可读纯文本，杜绝 "[object Object]" */
export function coerceReadablePlainText(value: unknown, maxLength = 80): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || /\[object Object\]/i.test(trimmed)) return '';
    const collapsed = trimmed.replace(/\s+/g, ' ');
    return collapsed.length > maxLength ? `${collapsed.slice(0, Math.max(1, maxLength - 1))}…` : collapsed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = coerceReadablePlainText(item, maxLength);
      if (nested) return nested;
    }
    return '';
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['main', 'title', 'text', 'content', 'label', 'summary', 'stem', 'question']) {
      const nested = coerceReadablePlainText(record[key], maxLength);
      if (nested) return nested;
    }
    if ('subQuestions' in record) {
      const nested = coerceReadablePlainText(record.subQuestions, maxLength);
      if (nested) return nested;
    }
  }
  return '';
}

export function needsResolvedSessionTitle(title: unknown): boolean {
  if (typeof title !== 'string') return true;
  const trimmed = title.trim();
  if (!trimmed || /\[object Object\]/i.test(trimmed)) return true;
  if (/^未命名颗粒/.test(trimmed)) return true;
  if (/^品题(\s|$)/i.test(trimmed)) return true;
  if (trimmed === '新品题' || trimmed === '品题会话') return true;
  if (/^[0-9a-f-]{8,}$/i.test(trimmed)) return true;
  // 题干/听写稿误当标题：需触发 ensureSessionTitle
  if (/^【\s*\d+\s*】/.test(trimmed)) return true;
  if (/请问/.test(trimmed)) return true;
  if (/□{2,}/.test(trimmed)) return true;
  if (/…\s*·|·\s*【/.test(trimmed)) return true;
  if (/[？?]/.test(trimmed) && (trimmed.includes('…') || trimmed.length >= 24)) return true;
  if (trimmed.length >= 22 && /[。；]/.test(trimmed)) return true;
  if (trimmed.includes('…') && trimmed.length >= 18) return true;
  // 空泛方法词，非能力主题
  if (/^(最不利情形|分类讨论建模|条件一般化|整除与余数|方程建模)$/.test(trimmed)) return true;
  return false;
}

export function pipelineStatusLabel(status: TtqPipelineStatus): string {
  switch (status) {
    case 'pending': return '排队中';
    case 'pap_running': return `${IMPLICIT_ABILITY_LABEL}提取中…`;
    case 'pap_failed': return '分析未完成';
    case 'analyzing': return '品位分析中…';
    case 'done': return '分析完成';
    default: return status;
  }
}

export function tasteVerdictLabel(verdict: TasteVerdict): string {
  switch (verdict) {
    case 'worth_tasting': return '值得品味';
    case 'marginal': return '边际可品';
    case 'not_worth': return '暂不建议';
    default: return verdict;
  }
}

export function formatTtqSessionProgress(particles: Array<{ pipelineStatus: TtqPipelineStatus }>) {
  const total = particles.length;
  const done = particles.filter((e) => e.pipelineStatus === 'done').length;
  const running = particles.filter((e) => (
    e.pipelineStatus === 'pending' || e.pipelineStatus === 'pap_running' || e.pipelineStatus === 'analyzing'
  )).length;
  const failed = particles.filter((e) => e.pipelineStatus === 'pap_failed').length;
  const parts = [`${total} 个颗粒`];
  if (done > 0) parts.push(`${done} 已完成`);
  if (running > 0) parts.push(`${running} 分析中`);
  if (failed > 0) parts.push(`${failed} 需重试`);
  return { total, done, running, failed, label: parts.join(' · ') };
}

export function resolveTtqParticleTitle(entry: Pick<TtqSessionParticleEntry, 'particleId' | 'particleTitle' | 'particleQuestions'>, index: number): string {
  const fromTitle = coerceReadablePlainText(entry.particleTitle, 80);
  if (fromTitle) return fromTitle;
  const fromQuestions = coerceReadablePlainText(entry.particleQuestions, 80)
    || formatParticlePreviewPlainText(entry.particleQuestions, 80);
  if (fromQuestions) return fromQuestions;
  return getParticleTitle({
    title: fromTitle || undefined,
    questions: entry.particleQuestions,
    id: entry.particleId,
  }) || `知识颗粒 ${index + 1}`;
}

export function resolveTtqParticleDisplayText(input: {
  particleId: string;
  particleTitle: string | null | undefined;
  particleQuestions: string | null | undefined;
}): string | null {
  const fromQuestions = coerceReadablePlainText(input.particleQuestions, 200)
    || formatParticlePreviewPlainText(input.particleQuestions, 200);
  if (fromQuestions) return fromQuestions;
  const fromTitle = coerceReadablePlainText(input.particleTitle, 120);
  return fromTitle || null;
}

/**
 * 品题会话主题：优先用后端 title；否则从原始颗粒标题提取展示名。
 */
export function resolveTtqSessionTitle(
  session: {
    title?: string | null;
    particles?: Array<Pick<TtqSessionParticleEntry, 'particleId' | 'particleTitle' | 'particleQuestions'>>;
    pathRuns?: Array<{ status?: string; derivedParticleIds?: string[] | null }>;
  },
): string {
  if (!needsResolvedSessionTitle(session.title)) {
    return coerceReadablePlainText(session.title, 48) || '新品题';
  }
  const counts = countTtqSessionParticles(session);
  const derivedIds = new Set<string>();
  for (const run of session.pathRuns ?? []) {
    if (run.status && run.status !== 'done') continue;
    for (const id of run.derivedParticleIds ?? []) {
      const trimmed = String(id || '').trim();
      if (trimmed) derivedIds.add(trimmed);
    }
  }
  const particles = session.particles ?? [];
  const originals = particles.filter((entry) => !derivedIds.has(entry.particleId));
  const source = originals.length > 0 ? originals : particles;
  if (source.length === 0) {
    return '新品题';
  }
  // 展示兜底：避免把题干截断当主题
  const usable = source
    .map((entry, index) => resolveTtqParticleTitle(entry, index).trim())
    .filter((label) => label && !needsResolvedSessionTitle(label));
  if (usable.length === 0) {
    return counts.original > 0 ? `品题会话（${counts.original} 题）` : '新品题';
  }
  const firstTitle = usable[0]!;
  const head = firstTitle.length > 18 ? `${firstTitle.slice(0, 18)}…` : firstTitle;
  return counts.original > 1 ? `${head} 等 ${counts.original} 题` : head;
}

/** 按 pathRuns.derivedParticleIds 区分会话内原始 / 衍生颗粒数量 */
export function countTtqSessionParticles(
  session: {
    particles?: Array<{ particleId: string }>;
    pathRuns?: Array<{ status?: string; derivedParticleIds?: string[] | null }>;
  },
): { original: number; derived: number; total: number } {
  const particles = session.particles ?? [];
  const derivedIds = new Set<string>();
  for (const run of session.pathRuns ?? []) {
    if (run.status && run.status !== 'done') continue;
    for (const id of run.derivedParticleIds ?? []) {
      const trimmed = String(id || '').trim();
      if (trimmed) derivedIds.add(trimmed);
    }
  }
  let derived = 0;
  let original = 0;
  for (const entry of particles) {
    if (derivedIds.has(entry.particleId)) derived += 1;
    else original += 1;
  }
  return { original, derived, total: particles.length };
}

/** 会话内原始知识颗粒 ID（排除 pathRuns 衍生） */
export function listTtqSessionOriginalParticleIds(
  session: {
    particles?: Array<{ particleId: string }>;
    pathRuns?: Array<{ status?: string; derivedParticleIds?: string[] | null }>;
  },
): string[] {
  const particles = session.particles ?? [];
  const derivedIds = new Set<string>();
  for (const run of session.pathRuns ?? []) {
    if (run.status && run.status !== 'done') continue;
    for (const id of run.derivedParticleIds ?? []) {
      const trimmed = String(id || '').trim();
      if (trimmed) derivedIds.add(trimmed);
    }
  }
  const out: string[] = [];
  for (const entry of particles) {
    const id = String(entry.particleId || '').trim();
    if (!id || derivedIds.has(id)) continue;
    out.push(id);
  }
  return out;
}

export function formatTtqSessionParticleCounts(counts: {
  original: number;
  derived: number;
}): string {
  return `${counts.original} 个原始颗粒 · ${counts.derived} 个衍生颗粒`;
}

/** 列表卡片：M 个知识颗粒压缩为 N 个品题浓缩结果 */
export function formatTtqSessionCompressionLine(input: {
  particleTotal: number;
  ipttqCount: number;
}): string {
  const particles = Math.max(0, Math.floor(Number(input.particleTotal) || 0));
  const ipttq = Math.max(0, Math.floor(Number(input.ipttqCount) || 0));
  return `${particles} 个知识颗粒压缩为 ${ipttq} 个品题浓缩结果`;
}

export function resolveTtqSessionIpttqCount(session: {
  ipttqCount?: number | null;
  ipttqRecords?: Array<{ lifecycle?: string } | null> | null;
}): number {
  if (typeof session.ipttqCount === 'number' && Number.isFinite(session.ipttqCount)) {
    return Math.max(0, Math.floor(session.ipttqCount));
  }
  const records = Array.isArray(session.ipttqRecords) ? session.ipttqRecords : [];
  return records.filter((item) => item && item.lifecycle !== 'archived').length;
}

export function ttqSessionNextStepHint(input: {
  particleCount: number;
  doneCount: number;
  runningCount: number;
  failedCount: number;
  aggregationAvailable: boolean;
}): string {
  if (input.particleCount === 0) return `点击添加知识颗粒，系统将自动提取${IMPLICIT_ABILITY_LABEL}。`;
  if (input.runningCount > 0) return '正在后台分析，完成后可查看品位结论。';
  if (input.failedCount > 0) return `有 ${input.failedCount} 个颗粒分析未完成，可重新提取。`;
  if (input.particleCount === 1) return '再添加 1 个颗粒并完成分析后，可挖掘共同隐性能力。';
  if (input.aggregationAvailable) return '各颗粒分析已完成，可发起聚合分析。';
  return '等待所有颗粒分析完成后，即可进行多颗粒聚合。';
}

export type TtqKnowledgeEntropyResult = {
  value: number | null;
  label: string;
  hint: string;
};

/** 基于已完成颗粒的品位得分分布估算知识熵（0–100%，越高表示颗粒间差异越大） */
export function computeTtqKnowledgeEntropy(
  particles: TtqSessionParticleEntry[],
): TtqKnowledgeEntropyResult {
  const scored = particles
    .filter((entry) => entry.pipelineStatus === 'done' && entry.taste?.scores)
    .map((entry) => {
      const scores = entry.taste!.scores;
      return (scores.crossLink + scores.antiPattern + scores.ideaExpression + scores.chainLength) / 4;
    });

  if (scored.length < 2) {
    return {
      value: null,
      label: '—',
      hint: scored.length === 0 ? '暂无已完成分析的颗粒' : '至少 2 个颗粒分析完成后可计算',
    };
  }

  const bins = 5;
  const counts = Array.from({ length: bins }, () => 0);
  for (const score of scored) {
    const clamped = Math.max(0, Math.min(0.999, score));
    counts[Math.floor(clamped * bins)] += 1;
  }

  const total = scored.length;
  let entropy = 0;
  for (const count of counts) {
    if (count <= 0) continue;
    const probability = count / total;
    entropy -= probability * Math.log2(probability);
  }

  const normalized = Math.log2(bins) > 0 ? entropy / Math.log2(bins) : 0;
  return {
    value: normalized,
    label: `${Math.round(normalized * 100)}%`,
    hint: `基于 ${scored.length} 个已完成颗粒的品位分布`,
  };
}

export function ttqSessionStatusLabel(status: 'draft' | 'active' | 'completed'): string {
  switch (status) {
    case 'draft': return '草稿';
    case 'active': return '进行中';
    case 'completed': return '已完成';
    default: return status;
  }
}
